import { useEvents } from '@/hooks/useEvents';
import { useTasks } from '@/hooks/useTasks';
import { useFamily } from '@/hooks/useFamily';
import { useMemo, useRef } from 'react';

interface UpcomingItem {
  type: 'event' | 'task';
  title: string;
  date: Date;
  id: string;
}

interface UsePersonUpcomingItemsReturn {
  upcomingItems: UpcomingItem[];
  hasUpcomingItems: boolean;
  upcomingCount: number;
}

export function usePersonUpcomingItems(
  personId?: string,
  profileId?: string,
  daysAhead: number = 7
): UsePersonUpcomingItemsReturn {
  const { family } = useFamily();
  const { events } = useEvents(family?.id);
  const { tasks } = useTasks(family?.id);
  
  // Cache the result to prevent unnecessary recalculations
  const cacheRef = useRef<{
    key: string;
    result: UsePersonUpcomingItemsReturn;
  } | null>(null);

  const upcomingItems = useMemo(() => {
    if (!personId && !profileId) return [];

    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + daysAhead);

    const items: UpcomingItem[] = [];

    // Check for upcoming events
    if (events) {
      events.forEach(event => {
        const eventDate = new Date(event.start_date);
        
        // Check if event is within the time range
        if (eventDate >= now && eventDate <= futureDate) {
          // Check if person is linked to this event
          const isLinked = event.linked_persons?.includes(personId || profileId || '');
          
          if (isLinked) {
            items.push({
              type: 'event',
              title: event.title,
              date: eventDate,
              id: event.id
            });
          }
        }
      });
    }

    // Check for upcoming tasks
    if (tasks) {
      tasks.forEach(task => {
        if (task.due_date) {
          const taskDate = new Date(task.due_date);
          
          // Check if task is within the time range and not completed
          if (taskDate >= now && taskDate <= futureDate && task.status !== 'completed') {
            // Check if person is assigned to this task
            const isAssigned = task.assigned_to_list?.some(assigneeId => {
              // Handle both profile:id and person:id formats, and raw IDs
              return assigneeId === personId || 
                     assigneeId === profileId ||
                     assigneeId === `profile:${profileId}` ||
                     assigneeId === `person:${personId}`;
            }) || task.assigned_to === personId || task.assigned_to === profileId;
            
            if (isAssigned) {
              items.push({
                type: 'task',
                title: task.title,
                date: taskDate,
                id: task.id
              });
            }
          }
        }
      });
    }

    // Sort by date (earliest first)
    return items.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [events, tasks, personId, profileId, daysAhead]);

  // Create cache key for this specific query
  const cacheKey = `${personId}-${profileId}-${daysAhead}-${events?.length}-${tasks?.length}`;
  
  // Return cached result if available and still valid
  if (cacheRef.current?.key === cacheKey) {
    return cacheRef.current.result;
  }

  // Calculate and cache new result
  const result = {
    upcomingItems,
    hasUpcomingItems: upcomingItems.length > 0,
    upcomingCount: upcomingItems.length
  };
  
  cacheRef.current = { key: cacheKey, result };
  return result;
}